import React, { useContext, useEffect, useState } from 'react';
import { BarsOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { RiskCategory } from '../../../models';
import { EntityChangeLog } from '../../reports';
import { UserContext } from '../../contexts';
import { RiskCategoryEditPage } from './risk-category-edit-page';

export function RiskCategoriesPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalRiskCategory, setEditModalRiskCategory] = useState<RiskCategory>();
  const [loading, setLoading] = useState(false);
  const [riskCategories, setRiskCategories] = useState<RiskCategory[]>();
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    loadRiskCategories();
  }, []);

  useEffect(() => {
    setCanEdit(hasPermission(user, 'riskCategories.edit'));
  }, [user]);

  const loadRiskCategories = () => {
    setLoading(true);
    apiClient
      .get('risk-categories')
      .then(response => {
        setRiskCategories(response.data as RiskCategory[]);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openEditModal = () => {
    setEditModalRiskCategory(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByRiskCategory = (riskCategory: RiskCategory) => {
    setEditModalRiskCategory(riskCategory);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const onRefreshClick = () => {
    loadRiskCategories();
  };

  const onUserAdded = () => {
    closeEditModal();
    loadRiskCategories();
  };

  const deleteRiskCategory = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`risk-categories/${id}`)
      .then(() => {
        setLoading(false);
        loadRiskCategories();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteRiskCategory = (id: string) => {
    const onOk = () => deleteRiskCategory(id);
    Modal.confirm({
      title: 'Risk kategorisini sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Risk kategorisini silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'edit') {
      const riskCategory = riskCategories?.find(m => m.id === id);
      if (riskCategory) {
        openEditModalByRiskCategory(riskCategory);
      } else {
        openEditModal();
      }
    } else if (e.key === 'changelog') {
      openChangeLogModal(id);
    } else if (e.key === 'del') {
      confirmDeleteRiskCategory(id);
    }
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      {canEdit && (
        <Menu.Item key="edit">
          <Text>Düzenle</Text>
        </Menu.Item>
      )}
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <Menu.Item key="del">
          <Text type="danger">Sil</Text>
        </Menu.Item>
      )}
    </Menu>
  );

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '',
      key: 'action',
      render: (_: any, riskCategory: RiskCategory) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(riskCategory.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <BarsOutlined /> Risk kategorileri
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button type="primary" key="sync" onClick={openEditModal} icon={<PlusOutlined />}>
                Yeni risk kategorisi
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} rowKey={row => row.id} dataSource={riskCategories} loading={loading} pagination={false} />
      </Content>
      <Modal
        title={editModalRiskCategory ? 'Risk kategorisini düzenle' : 'Yeni yaralanma yeri'}
        visible={editModalOpen}
        footer={null}
        onCancel={closeEditModal}
        destroyOnClose={true}
      >
        <RiskCategoryEditPage riskCategory={editModalRiskCategory} onSaved={onUserAdded} />
      </Modal>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'RiskCategory'} recordId={changeLogModalOpen} />
      </Modal>
    </>
  );
}
